// Deterministischer Deutsch-Datum-Parser für „Termin aus dieser Mail" (P2.16).
//
// Extrahiert aus Betreff+Body einen Termin-TAG als Vorbelegung für das
// Freie-Slots-Fenster des Kalender-Wizards. KEIN LLM, KEIN Egress — reine
// String-Mathematik, damit sie DOM-frei (Node) testbar ist und offline läuft.
//
// `now` ist INJIZIERBAR (Signatur-Pflicht): die Dry-Run-Abnahmetabelle pinnt
// `now = Do 2026-07-23`, damit die Fälle NICHT vom realen Laufdatum abhängen.
//
// Rückgabe: der erkannte Tag als lokale Mitternacht (`Date`) oder `null`.
// Der Aufrufer baut daraus das Slot-Fenster (erkannter Tag 07:00–19:00).
// Erkennungsklassen (in dieser Präzedenz):
//   1. ISO            YYYY-MM-DD                    (Jahr explizit)
//   2. numerisch      DD.MM.[YYYY]                  (Jahr optional → Rollover)
//   3. Monatsname     „3. September" / „September 3"(Jahr optional → Rollover)
//   4. relativ        heute/morgen/übermorgen/diese|nächste Woche
//   5. Wochentag      Mo–So                          (nächstes Vorkommen)
// Bloße Tag/Monat-Angaben in der Vergangenheit → nächstes Zukunfts-Vorkommen.

/** Kleinschreibung + deutsche Umlaute/ß → ASCII, damit „nächste"/„März"
 *  layoutunabhängig matchen. Ziffern/Punkte/Bindestriche bleiben unberührt. */
function norm(s: string): string {
  return (s || "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
}

function atMidnight(y: number, mZeroBased: number, d: number): Date {
  const x = new Date(y, mZeroBased, d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfDay(d: Date): Date {
  return atMidnight(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const x = startOfDay(d);
  x.setDate(x.getDate() + n);
  return x;
}

function isValidYmd(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const probe = new Date(y, m - 1, d);
  return probe.getMonth() === m - 1 && probe.getDate() === d;
}

/** Kandidat für Tag/Monat ohne Jahr → dieses Jahr, sonst nächstes, falls der
 *  Tag (auf Tages-Granularität) vor `now` läge. */
function withRollover(month: number, day: number, now: Date): Date | null {
  if (!isValidYmd(now.getFullYear(), month, day)) return null;
  const today = startOfDay(now);
  let cand = atMidnight(now.getFullYear(), month - 1, day);
  if (cand < today) cand = atMidnight(now.getFullYear() + 1, month - 1, day);
  return cand;
}

// Monatsname (normalisiert) → 1..12. Volle Namen + gängige Kürzel.
const MONTHS: Record<string, number> = {
  januar: 1, jan: 1,
  februar: 2, feb: 2,
  maerz: 3, mrz: 3,
  april: 4, apr: 4,
  mai: 5,
  juni: 6, jun: 6,
  juli: 7, jul: 7,
  august: 8, aug: 8,
  september: 9, sept: 9, sep: 9,
  oktober: 10, okt: 10,
  november: 11, nov: 11,
  dezember: 12, dez: 12,
};
// Alternation längster-Name-zuerst, damit „september" nicht von „sep" verkürzt wird.
const MONTH_ALT =
  "januar|jan|februar|feb|maerz|mrz|april|apr|mai|juni|jun|juli|jul|august|aug|" +
  "september|sept|sep|oktober|okt|november|nov|dezember|dez";

// Wochentag (normalisiert) → JS getDay (So=0 … Sa=6).
const WEEKDAYS: Record<string, number> = {
  sonntag: 0,
  montag: 1,
  dienstag: 2,
  mittwoch: 3,
  donnerstag: 4,
  freitag: 5,
  samstag: 6,
  sonnabend: 6,
};

export function parseGermanDate(text: string, now: Date): Date | null {
  const t = norm(text);
  if (!t.trim()) return null;

  // 1. ISO YYYY-MM-DD (Jahr explizit → kein Rollover).
  const iso = t.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/);
  if (iso) {
    const y = +iso[1];
    const m = +iso[2];
    const d = +iso[3];
    if (isValidYmd(y, m, d)) return atMidnight(y, m - 1, d);
  }

  // 2. numerisch DD.MM.[YYYY] (deutsche Konvention: trailing dot).
  const num = t.match(/\b(\d{1,2})\.(\d{1,2})\.(\d{4})?/);
  if (num) {
    const day = +num[1];
    const month = +num[2];
    if (num[3]) {
      const y = +num[3];
      if (isValidYmd(y, month, day)) return atMidnight(y, month - 1, day);
    } else {
      const r = withRollover(month, day, now);
      if (r) return r;
    }
  }

  // 3. Monatsname + Tag  („3. September" oder „September 3"), Jahr optional.
  const dayMonth = t.match(new RegExp(`\\b(\\d{1,2})\\.?\\s+(${MONTH_ALT})\\b`));
  const monthDay = t.match(new RegExp(`\\b(${MONTH_ALT})\\s+(\\d{1,2})\\b`));
  if (dayMonth) {
    const day = +dayMonth[1];
    const month = MONTHS[dayMonth[2]];
    const yr = t.match(new RegExp(`\\b\\d{1,2}\\.?\\s+${dayMonth[2]}\\s+(\\d{4})\\b`));
    if (yr && isValidYmd(+yr[1], month, day)) return atMidnight(+yr[1], month - 1, day);
    const r = withRollover(month, day, now);
    if (r) return r;
  }
  if (monthDay) {
    const month = MONTHS[monthDay[1]];
    const day = +monthDay[2];
    const r = withRollover(month, day, now);
    if (r) return r;
  }

  // 4. relative Anker (übermorgen VOR morgen).
  if (/\buebermorgen\b/.test(t)) return addDays(now, 2);
  if (/\bmorgen\b/.test(t)) return addDays(now, 1);
  if (/\bheute\b/.test(t)) return startOfDay(now);
  if (/\b(naechste|kommende)\s+woche\b/.test(t)) {
    // Montag der nächsten Woche (Wochen-Anker).
    const dow = now.getDay();
    const daysToMonday = ((1 - dow + 7) % 7) || 7;
    return addDays(now, daysToMonday);
  }
  if (/\bdiese\s+woche\b/.test(t)) {
    // „diese Woche" → ab heute (der erkannte Tag ist heute).
    return startOfDay(now);
  }

  // 5. Wochentag → nächstes Vorkommen (delta 1..7; gleicher Tag → +7).
  const wd = t.match(new RegExp(`\\b(${Object.keys(WEEKDAYS).join("|")})\\b`));
  if (wd) {
    const target = WEEKDAYS[wd[1]];
    const dow = now.getDay();
    const delta = ((target - dow + 7) % 7) || 7;
    return addDays(now, delta);
  }

  return null;
}
